import React from 'react';
import { Avatar } from '@onward-delivery/design';
import { PARTNERSHIP_STATUS, UNKNOWN_PARTNERSHIP_STATUS } from '../constants';
import {
    formatShipperAddress,
    formatShipperPhone,
    getConnectionTypeLabel,
    getInitials,
    getReadableTextColor,
    getShipperBranding,
    getShipperName,
} from '../utils';

const NOT_PROVIDED = 'Not provided';

/** Header navy from the ENG-7831 design, used when the shipper has no background color. */
const DEFAULT_HEADER_COLOR = '#1c1a31';

/** Text classes for a dark background (white text) and a light one (dark text). */
const TONES = {
    dark: { muted: 'text-white/90', strong: 'text-white', status: 'text-white/80', type: 'text-[#8fa2c7]' },
    light: { muted: 'text-foreground/80', strong: 'text-foreground', status: 'text-foreground/70', type: 'text-muted' },
};

const HeaderLine = ({ label, tone, children }) => (
    <span className={`break-words text-sm ${tone.muted}`}>
        {label}: <span className={`font-semibold ${tone.strong}`}>{children || NOT_PROVIDED}</span>
    </span>
);

/**
 * The fixed header on a shipper's profile, on the shipper's branding color (navy when unset).
 * @param {{ partnership: Object, setup?: { webhook: string, edi: string, integrations: string } | null }} props
 *   `setup` values are `SETUP_STATE` keys; null (or missing) while unknown, which shows "Type: —"
 */
const ShipperHeader = ({ partnership, setup }) => {
    const { shipper } = partnership;
    const name = getShipperName(partnership);
    const { logo, color } = getShipperBranding(partnership);
    const background = color || DEFAULT_HEADER_COLOR;
    const tone = getReadableTextColor(background) === '#000000' ? TONES.light : TONES.dark;
    const status = PARTNERSHIP_STATUS[partnership.status] || UNKNOWN_PARTNERSHIP_STATUS;

    return (
        <div
            className="flex items-start justify-between gap-6 rounded-xl px-6 py-5"
            style={{ backgroundColor: background }}
            data-testid="shipper-header"
        >
            <div className="flex min-w-0 items-center gap-6">
                <Avatar
                    src={logo || undefined}
                    alt={name}
                    fallback={getInitials(name)}
                    className="h-24 w-24 shrink-0 bg-[#e3e3e3] text-4xl font-bold text-black"
                />
                <div className="flex min-w-0 flex-col gap-0.5">
                    <h2 className={`mb-1 text-xl font-semibold ${tone.strong}`}>{name}</h2>
                    <HeaderLine label="Network ID" tone={tone}>
                        {shipper?.client_id}
                    </HeaderLine>
                    <HeaderLine label="Address" tone={tone}>
                        {formatShipperAddress(shipper)}
                    </HeaderLine>
                    <HeaderLine label="Email" tone={tone}>
                        {shipper?.email && (
                            <a href={`mailto:${shipper.email}`} className="break-all text-primary underline">
                                {shipper.email}
                            </a>
                        )}
                    </HeaderLine>
                    <HeaderLine label="Phone" tone={tone}>
                        {formatShipperPhone(shipper)}
                    </HeaderLine>
                </div>
            </div>
            <div className="flex shrink-0 flex-col items-center gap-2">
                <span
                    className={`flex items-center gap-3 rounded-md border-2 border-current px-4 py-1.5 text-base font-semibold ${
                        status.className || tone.status
                    }`}
                >
                    <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-current" />
                    {status.label}
                </span>
                <span className={`text-sm font-semibold ${tone.type}`}>
                    Type: <span className="text-primary">{getConnectionTypeLabel(setup)}</span>
                </span>
            </div>
        </div>
    );
};

export default ShipperHeader;
